import React, { useState } from "react";
import styled from "styled-components";
import { Icon } from "@iconify/react";

// A read-only JSON view: collapsible objects/arrays with type-coloured primitives.

const Wrapper = styled.div`
  font-family: monospace;
  font-size: 12px;
  line-height: 1.6;
  color: var(--main-font-color);
`;

const Node = styled.div`
  padding-left: ${(props) => (props.$depth ? "14px" : "0")};
`;

const Toggle = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 0;
  border: none;
  background: transparent;
  color: var(--main-font-color);
  font-family: inherit;
  font-size: inherit;
  cursor: pointer;
`;

const Key = styled.span`
  color: #a0a6b1;
`;

const StringValue = styled.span`
  color: #7ee787;
  word-break: break-word;
`;

const NumberValue = styled.span`
  color: #79c0ff;
`;

const BooleanValue = styled.span`
  color: #ffa657;
`;

const NullValue = styled.span`
  color: #a0a6b1;
`;

const Bracket = styled.span`
  color: #a0a6b1;
`;

const primitive = (value) => {
  if (value === null) return <NullValue>null</NullValue>;
  if (typeof value === "string") return <StringValue>&quot;{value}&quot;</StringValue>;
  if (typeof value === "number") return <NumberValue>{String(value)}</NumberValue>;
  if (typeof value === "boolean") return <BooleanValue>{String(value)}</BooleanValue>;

  return <StringValue>{String(value)}</StringValue>;
};

/**
 * @param {object} props
 * @param {*} props.value
 * @param {string} [props.name] The key this value sits under.
 * @param {number} [props.depth]
 */
export const JsonTree = ({ value, name = null, depth = 0 }) => {
  const isObject = value !== null && typeof value === "object" && !Array.isArray(value);
  const isArray = Array.isArray(value);
  const [open, setOpen] = useState(depth < 3);

  if (!isObject && !isArray) {
    return (
      <Node $depth={depth}>
        {name !== null ? <Key>{name}: </Key> : null}
        {primitive(value)}
      </Node>
    );
  }

  const entries = isArray ? value.map((entry, index) => [index, entry]) : Object.entries(value);
  const close = isArray ? "]" : "}";
  const openBracket = isArray ? "[" : "{";

  return (
    <Wrapper>
      <Node $depth={depth}>
        <Toggle type="button" onClick={() => setOpen((current) => !current)}>
          <Icon icon={open ? "lucide:chevron-down" : "lucide:chevron-right"} />
          {name !== null ? <Key>{name}: </Key> : null}
          <Bracket>{openBracket}</Bracket>
          {!open ? (
            <>
              <span style={{ color: "#a0a6b1" }}>
                {entries.length} {isArray ? "item" : "key"}
                {entries.length === 1 ? "" : "s"}
              </span>
              <Bracket>{close}</Bracket>
            </>
          ) : null}
        </Toggle>

        {open
          ? entries.map(([key, entry]) => (
              <JsonTree key={String(key)} value={entry} name={String(key)} depth={depth + 1} />
            ))
          : null}

        {open ? (
          <Node $depth={depth}>
            <Bracket>{close}</Bracket>
          </Node>
        ) : null}
      </Node>
    </Wrapper>
  );
};

export default JsonTree;
